// @ts-nocheck
import * as React from "react";
import { ManualMobileShell, Section, Lead, Steps, Note, GoTo, ShotMobile, FlowStripMobile } from "./manual-shell";
import { supportPhone } from "./common";

// 어르신(citizen) 전용 사용자 매뉴얼 — 1인칭·큰 글씨 친화 톤의 모바일 인앱 가이드.
// 보호자·어르신 공용 매뉴얼(manual-guardian.tsx)과 달리, 어르신 본인이 매일 쓰는
// 화면만 1인칭("나"·"내")으로 짧고 쉽게 다룬다. 진입은 홈(/home)·도움말(/help)의
// "사용자 매뉴얼" 링크. 하단 5탭은 어르신용 ElderTabBar(홈·일정·복약·알림·내 정보).
// 셸/프리미티브는 manual-shell.tsx(ManualMobileShell·ShotMobile)에서 가져온다.

// 실제 화면 스크린샷 경로 — playwright test e2e/manual-shots/elder.gen.spec.ts 로
// 생성(public/manual-shots/elder/, 세로 390×844 폰 캡처 · 어르신 본인 계정 기준).
const S = (key) => `/manual-shots/elder/${key}.png`;

const TOC = [
  { id: "start",    label: "시작하기" },
  { id: "home",     label: "홈" },
  { id: "schedule", label: "방문 일정" },
  { id: "meds",     label: "복약" },
  { id: "notify",   label: "알림" },
  { id: "sos",      label: "응급 SOS" },
  { id: "me",       label: "내 정보" },
  { id: "help",     label: "도움말·문의" },
];

const INTRO = (
  <>
    이 안내서는 어르신께서 휴대폰으로 <b>오늘 방문·복약·알림</b>을 확인하시는 방법을 쉬운 말로 정리했습니다.
    위급할 때 도움을 청하는 <b>응급 SOS</b>도 함께 안내합니다.
    화면 아래쪽의 <b>5개 단추(홈·일정·복약·알림·내 정보)</b>를 눌러 이동하시면 됩니다.
  </>
);

function ElderManual() {
  return (
    <ManualMobileShell
      nav="elder"
      tab="home"
      homeHref="/home"
      badge="ELDER GUIDE · 어르신용"
      title="어르신 사용자 매뉴얼"
      revised="최종 개정 2026-06-18 · 어르신(citizen) 본인 기준"
      intro={INTRO}
      toc={TOC}
    >
      <Section id="start" title="1. 시작하기">
        <Lead>휴대폰으로 오늘 방문과 복약, 새 소식을 확인하실 수 있습니다. 화면 아래 5개 단추로 이동하시고, 막히실 때는 언제든 '도움말'을 누르세요.</Lead>
        <Steps items={[
          "받으신 계정으로 로그인합니다.",
          "화면 아래 5개 단추로 이동합니다 — 홈 · 일정 · 복약 · 알림 · 내 정보.",
          "위급할 때는 홈 화면의 빨간 '응급SOS' 단추를 누릅니다(6장).",
          "잘 모르실 때는 '도움말'에서 상담센터로 전화하거나 이 안내서를 다시 봅니다(8장).",
        ]} />
        <Note>어르신은 로그인하면 <b>홈 화면(/home)</b>이 먼저 열립니다. 글씨가 크고 단추가 큼직하게 되어 있어요. 천천히 눌러 보셔도 괜찮습니다.</Note>
        <GoTo links={[{ href: "/home", label: "홈 열기" }]} />
      </Section>

      <Section id="home" title="2. 홈 (오늘 방문)">
        <Lead>홈 화면에서 오늘 누가, 언제 방문하는지 한눈에 보실 수 있습니다.</Lead>
        <Steps items={[
          "맨 위에서 오늘 방문 시각과 담당 매니저를 확인합니다.",
          "담당 매니저 칸의 전화 단추로 바로 통화할 수 있습니다.",
          "아래로 내리면 이번 주 방문 일정과 도움말이 보입니다.",
          "위급할 때는 빨간 '응급SOS' 단추를 누릅니다(6장).",
        ]} />
        <ShotMobile src={S("home")} alt="어르신 홈 화면" caption="홈 — 오늘 방문·담당 매니저·응급SOS (눌러서 크게 보기)" />
        <GoTo links={[{ href: "/home", label: "홈 열기" }]} />
      </Section>

      <Section id="schedule" title="3. 방문 일정">
        <Lead>아래 '일정' 단추를 누르면 지난 방문과 다음 방문을 시간 순서로 보실 수 있습니다.</Lead>
        <Steps items={[
          "이번 주 방문 횟수와 시간을 확인합니다.",
          "방문 목록에서 예정·완료된 방문을 차례로 봅니다.",
          "혈압·맥박 같은 건강 기록을 확인합니다.",
          "방문이 끝나면 '별점 평가'로 매니저에게 별점과 한마디를 남길 수 있습니다.",
        ]} />
        <Note>어르신 화면에서는 일부 자세한 케이스 정보는 보호자에게만 보이고, 어르신께는 꼭 필요한 안내만 보여 드립니다.</Note>
        <ShotMobile src={S("timeline")} alt="방문 일정 화면" caption="일정 — 방문 기록·이번 주 요약·별점 평가" />
        <GoTo links={[{ href: "/m/timeline", label: "일정 열기" }]} />
      </Section>

      <Section id="meds" title="4. 복약">
        <Lead>아래 '복약' 단추에서 오늘 드실 약을 시간대(아침·점심·저녁·자기 전)별로 확인하고 체크합니다.</Lead>
        <Steps items={[
          "시간대별로 드셔야 할 약을 봅니다.",
          "약을 드셨으면 동그라미를 눌러 '먹음'으로 표시합니다.",
          "중단된 약은 아래쪽에 따로 모여 있습니다.",
        ]} />
        <Note>복약 시간이 되면 알림으로도 알려 드립니다(5장). 약에 대해 궁금하시면 담당 매니저나 상담센터에 물어보세요.</Note>
        <ShotMobile src={S("meds")} alt="복약 화면" caption="복약 — 시간대별 약·먹음 표시" />
        <GoTo links={[{ href: "/m/meds", label: "복약 열기" }]} />
      </Section>

      <Section id="notify" title="5. 알림">
        <Lead>아래 '알림' 단추에서 방문·복약·메시지 같은 새 소식을 확인합니다.</Lead>
        <Steps items={[
          "알림은 날짜별(오늘·어제·이전)로 모여 있습니다.",
          "위쪽 단추로 보고 싶은 종류만 골라 봅니다.",
          "'모두 읽음'을 누르면 한 번에 정리됩니다.",
        ]} />
        <Note>알림을 받을지 여부는 '내 정보'에서 종류별로 켜고 끌 수 있습니다(7장).</Note>
        <ShotMobile src={S("notify")} alt="알림 화면" caption="알림 — 날짜별 새 소식·모두 읽음" />
        <GoTo links={[{ href: "/m/notify", label: "알림 열기" }]} />
      </Section>

      <Section id="sos" title="6. 응급 SOS">
        <Lead>넘어지셨거나 갑자기 도움이 필요할 때, 홈 화면의 빨간 '응급SOS' 단추로 즉시 도움을 청할 수 있습니다. 누르면 가까운 소방·병원·보건소와 담당자에게 바로 연락이 갑니다.</Lead>
        <Steps items={[
          "지금 상황을 4가지 중에서 고릅니다 — 넘어졌어요 / 도움이 필요해요 / 마음이 힘들어요 / 위협받고 있어요.",
          "상황을 한두 줄 적습니다(어려우시면 비워 두셔도 됩니다).",
          "큰 단추를 누르면 접수되고, 연결된 기관과 전화번호가 화면에 나옵니다.",
        ]} />
        <Note>숨이 차거나 정말 위급하시면 <b>먼저 119</b>에 전화하세요. 응급 SOS는 담당자와 가까운 기관에 상황을 함께 알리기 위한 기능입니다.</Note>
        <ShotMobile src={S("sos")} alt="응급 SOS 화면" caption="응급 SOS — 상황 고르기·접수하면 기관 연결" />
        <GoTo links={[{ href: "/m/sos", label: "응급 SOS 열기" }]} />
      </Section>

      <Section id="me" title="7. 내 정보">
        <Lead>아래 '내 정보' 단추에서 사진과 연락처를 바꾸고, 알림을 켜고 끌 수 있습니다.</Lead>
        <Steps items={[
          "프로필 사진을 올리거나 바꿉니다.",
          "이름·연락처를 고치고, 필요하면 비밀번호를 바꿉니다.",
          "알림 설정에서 받을 소식의 종류를 켜고 끕니다.",
          "'도움말'로 들어가 상담센터에 전화하거나 이 안내서를 다시 봅니다(8장).",
        ]} />
        <ShotMobile src={S("me")} alt="내 정보 화면" caption="내 정보 — 사진·이름·연락처·알림 설정" />
        <GoTo links={[{ href: "/m/guardian-me", label: "내 정보 열기" }]} />
      </Section>

      <Section id="help" title="8. 도움말 · 문의">
        <Lead>막히시거나 사람과 직접 이야기하고 싶으실 때 '도움말'을 누르세요. 가장 크게 보이는 초록색 단추가 상담센터 전화입니다.</Lead>
        <Steps items={[
          "홈 화면이나 '내 정보'에서 '도움말'을 누릅니다.",
          "초록색 '상담센터 전화' 단추로 바로 통화합니다.",
          "오늘 방문·일정·알림·내 정보로 가는 큰 단추도 여기 모여 있습니다.",
        ]} />
        <ShotMobile src={S("help")} alt="도움말 화면" caption="도움말 — 상담센터 전화·큰 도움 단추" />
        <div style={{ marginTop: 16, padding: 14, background: "var(--bg-sub)", borderRadius: 12, fontSize: 13, color: "var(--ink-2)", lineHeight: 1.8 }}>
          <div style={{ fontWeight: 800, color: "var(--ink-1)", marginBottom: 4 }}>상담센터</div>
          {supportPhone()} (평일 09:00–18:00)<br />
          전화가 어려우시면 담당 매니저에게 말씀하셔도 됩니다.
        </div>
        <GoTo links={[{ href: "/help", label: "도움말 열기" }]} />
      </Section>
    </ManualMobileShell>
  );
}

export { ElderManual };
